/**
 * ADLSignatureModal.jsx
 * CCH Care — Phase 3
 * Last Modified: 2026-06-11
 *
 * Changes:
 * - Added hygiene incomplete warning step (same pattern as meals/safety)
 *   Props: hygieneDocumentedCount, hygieneTotalCount
 *
 * Meals warning removed (2026-06-11):
 *   The section-level "Meals Incomplete" warning was a vestige of the
 *   pre-per-meal-signing era. This modal only opens for section='meals'
 *   AFTER all core meals are signed (MealsForm fires onSign('meals') only
 *   on data.section_signed), so the warning could never legitimately fire
 *   — it only ever surfaced optional snacks or a phantom meal type
 *   (snack_hs) that MealsForm has no entry for and can never document.
 *   Meal completeness (incl. per-meal "N/A – Outside Shift" and Refused)
 *   is owned entirely by MealsForm now. SHIFT_MEALS / MEAL_LABELS and the
 *   documentedMeals/shiftType props are gone.
 *
 * Warning inputs snapshotted at open (2026-06-11):
 *   useADLRecord auto-refreshes sections every 60s. The hygiene/safety
 *   warning counts are derived from those sections, so a background tick
 *   could mutate an OPEN warning out from under the reviewer. We now
 *   capture the counts when the modal opens and compute the warning from
 *   that snapshot — the dialog is a blocking overlay, so nothing under it
 *   changes except the auto-refresh we want to freeze out.
 */

import { useState, useEffect, useRef } from 'react';
import { COLORS } from '../../config/constants';

function decodeHtml(str) {
  if (!str) return str;
  return str
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"');
}

const SECTION_LABELS = {
  hygiene:        { label: 'Hygiene',       icon: '🛁' },
  meals:          { label: 'Meals',         icon: '🍽️' },
  repositioning:  { label: 'Repositioning', icon: '🔄' },
  fluid_intake:   { label: 'Fluid Intake',  icon: '💧' },
  bowel:          { label: 'Bowel',         icon: '📋' },
  urination:      { label: 'Urination',     icon: '🚽' },
  skin_checks:    { label: 'Skin Checks',   icon: '👁️' },
  safety_checks:  { label: 'Safety Checks', icon: '✅' },
};

export default function ADLSignatureModal({
  isOpen,
  section,
  signerName,
  signing = false,
  error = null,
  totalSafetyItems = 0,
  checkedSafetyItems = 0,
  hygieneDocumentedCount = 0,
  hygieneTotalCount = 0,
  onConfirm,
  onCancel,
}) {
  const [notes, setNotes] = useState('');
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);

  // Snapshot the completeness inputs at the moment the modal opens, so the
  // 60s useADLRecord auto-refresh can't mutate an open warning. liveRef
  // keeps the latest props without making them effect deps (exhaustive-deps
  // safe — we intentionally snapshot at open only).
  const [snapshot, setSnapshot] = useState({
    checkedSafetyItems, totalSafetyItems,
    hygieneDocumentedCount, hygieneTotalCount,
  });

  const liveRef = useRef(snapshot);
  liveRef.current = {
    checkedSafetyItems, totalSafetyItems,
    hygieneDocumentedCount, hygieneTotalCount,
  };

  useEffect(() => {
    if (isOpen) {
      setNotes('');
      setWarningAcknowledged(false);
      setSnapshot({ ...liveRef.current });
    }
  }, [isOpen, section]);

  if (!isOpen || !section) return null;

  const sectionInfo = SECTION_LABELS[section] ?? { label: section, icon: '📋' };

  const uncheckedSafetyCount = section === 'safety_checks'
    ? Math.max(0, snapshot.totalSafetyItems - snapshot.checkedSafetyItems)
    : 0;

  const undocumentedHygieneCount = section === 'hygiene'
    ? Math.max(0, snapshot.hygieneTotalCount - snapshot.hygieneDocumentedCount)
    : 0;

  const showWarning = (
    uncheckedSafetyCount > 0 ||
    undocumentedHygieneCount > 0
  ) && !warningAcknowledged;

  function handleConfirm() {
    onConfirm?.(notes.trim() || null);
  }

  function warningTitle() {
    if (section === 'hygiene') return 'Hygiene Incomplete';
    return 'Safety Checks Incomplete';
  }

  function warningBody() {
    if (section === 'hygiene') {
      return `${undocumentedHygieneCount} of ${snapshot.hygieneTotalCount} hygiene tasks have not been documented. All tasks should be recorded each shift.`;
    }
    return `${uncheckedSafetyCount} of ${snapshot.totalSafetyItems} safety items have not been checked. All safety items should be verified each shift.`;
  }

  function warningHint() {
    if (section === 'hygiene') {
      return 'Go back to complete all tasks, or sign anyway if tasks were not applicable for this resident.';
    }
    return 'Go back to complete all items, or sign anyway if items were verified verbally.';
  }

  return (
    <>
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        onClick={onCancel}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onCancel()}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: COLORS.surface, borderRadius: '16px 16px 0 0',
        padding: '24px 20px 36px', zIndex: 1001,
        maxWidth: 480, margin: '0 auto',
      }}>
        {/* Handle */}
        <div style={{
          width: 36, height: 4, background: COLORS.border,
          borderRadius: 999, margin: '0 auto 20px',
        }} />

        {/* ── WARNING STEP ── */}
        {showWarning ? (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>⚠️</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: COLORS.text }}>
                {warningTitle()}
              </div>
              <div style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 6 }}>
                {warningBody()}
              </div>
            </div>

            {undocumentedHygieneCount > 0 && (
              <div style={{
                background: COLORS.warningLight,
                border: `1px solid ${COLORS.warning}44`,
                borderRadius: 10, padding: '12px 16px', marginBottom: 20,
                fontSize: 13, color: COLORS.text, fontWeight: 600, textAlign: 'center',
              }}>
                {snapshot.hygieneDocumentedCount} / {snapshot.hygieneTotalCount} tasks documented
              </div>
            )}

            {uncheckedSafetyCount > 0 && (
              <div style={{
                background: COLORS.warningLight,
                border: `1px solid ${COLORS.warning}44`,
                borderRadius: 10, padding: '12px 16px', marginBottom: 20,
                fontSize: 13, color: COLORS.text, fontWeight: 600, textAlign: 'center',
              }}>
                {snapshot.checkedSafetyItems} / {snapshot.totalSafetyItems} items checked
              </div>
            )}

            <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 20, textAlign: 'center' }}>
              {warningHint()}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onCancel} style={{
                flex: 1, padding: '12px 0',
                background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                borderRadius: 10, fontSize: 14, fontWeight: 600,
                color: COLORS.textMuted, cursor: 'pointer',
              }}>
                Go Back
              </button>
              <button onClick={() => setWarningAcknowledged(true)} style={{
                flex: 2, padding: '12px 0',
                background: COLORS.warning, border: 'none', borderRadius: 10,
                fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer',
              }}>
                Sign Anyway
              </button>
            </div>
          </>
        ) : (
          <>
            {/* ── NORMAL SIGN STEP ── */}
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>{sectionInfo.icon}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>
                Sign {sectionInfo.label}?
              </div>
              <div style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 6 }}>
                This will lock the section. Changes after signing require an amendment.
              </div>
            </div>

            {warningAcknowledged && undocumentedHygieneCount > 0 && (
              <div style={{
                background: COLORS.warningLight, border: `1px solid ${COLORS.warning}44`,
                borderRadius: 8, padding: '8px 12px', marginBottom: 12,
                fontSize: 12, color: COLORS.warning, fontWeight: 600,
              }}>
                ⚠️ Signing with {undocumentedHygieneCount} hygiene task{undocumentedHygieneCount > 1 ? 's' : ''} not documented
              </div>
            )}
            {warningAcknowledged && uncheckedSafetyCount > 0 && (
              <div style={{
                background: COLORS.warningLight, border: `1px solid ${COLORS.warning}44`,
                borderRadius: 8, padding: '8px 12px', marginBottom: 12,
                fontSize: 12, color: COLORS.warning, fontWeight: 600,
              }}>
                ⚠️ Signing with {uncheckedSafetyCount} safety item{uncheckedSafetyCount > 1 ? 's' : ''} unchecked
              </div>
            )}

            <div style={{
              background: COLORS.primaryLight, border: `1px solid ${COLORS.primaryAlpha}`,
              borderRadius: 10, padding: '12px 16px', marginBottom: 16,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 20 }}>👤</span>
              <div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 600 }}>SIGNING AS</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.primary }}>{decodeHtml(signerName)}</div>
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 20 }}>
              <label htmlFor="sig-modal-notes" style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 6 }}>
                Notes (optional)
              </label>
              <textarea
                id="sig-modal-notes"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add any notes about this section…"
                rows={2}
                style={{
                  width: '100%', padding: '10px 12px',
                  border: `1px solid ${COLORS.border}`, borderRadius: 8,
                  fontSize: 14, color: COLORS.text, resize: 'none',
                  boxSizing: 'border-box', fontFamily: 'inherit',
                }}
              />
            </div>

            {error && (
              <div style={{
                background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerAlpha}`,
                borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                fontSize: 13, color: COLORS.danger, fontWeight: 600,
              }}>
                ⚠️ {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onCancel} disabled={signing} style={{
                flex: 1, padding: '12px 0',
                background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                borderRadius: 10, fontSize: 14, fontWeight: 600,
                color: COLORS.textMuted, cursor: 'pointer',
              }}>
                Cancel
              </button>
              <button onClick={handleConfirm} disabled={signing} style={{
                flex: 2, padding: '12px 0',
                background: signing ? COLORS.border : COLORS.primary,
                border: 'none', borderRadius: 10,
                fontSize: 14, fontWeight: 700,
                color: signing ? COLORS.textMuted : '#fff',
                cursor: signing ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
              }}>
                {signing ? 'Signing…' : 'Sign & Lock Section ✍️'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}